/** @prettier */

import {Box, Stack, Paper, IconButton, Container, Grid} from '@mui/material';
import {useLoaderData, useParams} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import {Calender} from './Calender';
import {DayOfWeekSettingDialog} from './DayOfWeekSettingDialog';
import {DesignatedDaySettingDialog} from './DesignatedDaySettingDialog';
import {DesignatedDayDeleteDialog} from './DesignatedDayDeleteDialog';
import {AddButton} from '../../component/BasicButton';
//API import
import {
  getCompositionsSitesBySiteID,
  getSiteCalender,
  getSiteCalenderRoutine,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';

import {iconBlack, iconWhite, limit} from '../../define';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';

/** 月名一覧 */
export const monthTable: (keyof apiIF.res.getSiteCalender)[] = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

/** 曜日一覧 */
export const dayOfWeekTable: (keyof apiIF.res.getSiteCalenderRoutine['weekly'])[] =
  [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

/** 週番号一覧 */
export const weekNumberTable = [
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'last',
];

type LoaderReturnType = {
  /** サイト名称 */
  siteName: string;
  /**タイムゾーン */
  timeZone: string;
  /** 1年分の特定日データ */
  calender: apiIF.res.getSiteCalender;
  /** 標準設定 */
  calenderRoutine: apiIF.res.getSiteCalenderRoutine;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //カレンダ情報の取得
    promises.push(getSiteCalender(params.siteID));
    promises.push(getSiteCalenderRoutine(params.siteID));
    //ユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));
    const response = await Promise.all(promises);

    const siteInfo = response[0] as apiIF.res.getCompositionsSitesBySiteID;
    const calender = response[1] as apiIF.res.getSiteCalender;
    const calenderRoutine = response[2] as apiIF.res.getSiteCalenderRoutine;
    const unitExistence = response[3] as unitExistence;

    if (
      unitExistence.hasAirconIU === false ||
      (siteInfo['site-authority'] !== 'admin' &&
        siteInfo['site-authority'] !== 'manager')
    ) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    return {
      siteName: siteInfo['site-name'],
      timeZone: siteInfo['timezone'],
      calender: calender,
      calenderRoutine: calenderRoutine,
    };
  } catch {
    return undefined;
  }
}

/**
 * 年間スケジュール、標準設定
 * @returns JSX.Element
 */
const ScheduleSetting = () => {
  const trans = useTranslation().t; //翻訳関数
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  /** パスパラメータ取得 */
  const {siteID} = useParams();

  /** タイムゾーン */
  const [timeZone, setTimeZone] = useState('');
  //prettier-ignore
  /** 年間スケジュール情報 */
  const [calenderData, setCalenderData] = useState<apiIF.res.getSiteCalender | null>(null);
  //prettier-ignore
  /** 標準設定情報 */
  const [calenderRoutineData, setCalenderRoutineData] = useState<apiIF.res.getSiteCalenderRoutine | null>(null);

  //フラグ
  /** 年間スケジュール変更があるかどうか */
  const [hasChangedYearSchedule, setHasChangedYearSchedule] = useState(false);
  /** 標準設定変更があるかどうか */
  const [hasChangedRoutine, setHasChangedRoutine] = useState(false);
  //prettier-ignore
  /** 曜日ごとの編集ダイアログの表示 */
  const [isOpenDayOfWeekSettingDialog, setIsOpenDayOfWeekSettingDialog] = useState(false);
  //prettier-ignore
  /** 指定日ごとの追加ダイアログの表示 */
  const [isOpenDesignatedDayAddDialog, setIsOpenDesignatedAddEditDialog] = useState(false);
  /** API応答待ち */
  const [apiWait, setAPIWait] = useState(false);

  useEffect(() => {
    if (loaderData === undefined) {
      return;
    }
    setTimeZone(loaderData.timeZone);
    setCalenderData(loaderData.calender);
    setCalenderRoutineData(loaderData.calenderRoutine);
  }, [loaderData]);

  //年間スケジュール変更時
  useEffect(() => {
    if (siteID === undefined || hasChangedYearSchedule === false) {
      return;
    }

    setAPIWait(true);
    //日毎の設定取得
    getSiteCalender(siteID)
      .then(response => {
        setCalenderData(response);
        setHasChangedYearSchedule(false);
      })
      .finally(() => {
        setAPIWait(false);
      });
  }, [hasChangedYearSchedule, siteID]);

  //標準設定変更時
  useEffect(() => {
    if (siteID === undefined || hasChangedRoutine === false) {
      return;
    }

    setAPIWait(true);
    //標準設定の取得
    getSiteCalenderRoutine(siteID)
      .then(response => {
        setCalenderRoutineData(response);
      })
      .finally(() => {
        setAPIWait(false);
        setHasChangedRoutine(false);
      });
  }, [hasChangedRoutine, siteID]);

  /**
   * 曜日毎の設定
   * @returns JSX.Element
   */
  const DayOfWeekSetting = () => {
    if (calenderRoutineData === null) {
      return <></>;
    }

    return (
      <Box>
        <Grid container rowSpacing={2} alignItems="center">
          {/** タイトル */}
          <Grid item xs>
            <h2>
              {trans('ScheduleSetting.defaultSetting.dayOfWeekSetting.title')}
            </h2>
          </Grid>
          {/** 編集アイコン */}
          <Grid item xs="auto">
            <IconButton
              size="medium"
              onClick={() => setIsOpenDayOfWeekSettingDialog(true)}
              disabled={apiWait}>
              <Box className="schedule__yearSchedule__editIcon__border">
                <img
                  src={iconBlack.EDIT.src}
                  alt={`${trans(
                    `ScheduleSetting.defaultSetting.image.alternativeText.${iconBlack.EDIT.alt}`
                  )}`}
                  title={`${trans(
                    `ScheduleSetting.defaultSetting.image.title.${iconWhite.EDIT.title}`
                  )}`}
                  className="schedule__yearSchedule__editIcon__border__image"
                />
              </Box>
            </IconButton>
          </Grid>
          {/** 一覧 */}
          <Grid item xs={12} container>
            {dayOfWeekTable.map(value => {
              /** 適用するクラス名 */
              const className = ((
                dayType:
                  | 'weekday'
                  | 'holiday'
                  | 'special-day-1'
                  | 'special-day-2'
              ) => {
                switch (dayType) {
                  case 'weekday':
                    return 'schedule__yearSchedule__dayOfWeek__item--weekday';
                  case 'holiday':
                    return 'schedule__yearSchedule__dayOfWeek__item--holiday';
                  case 'special-day-1':
                    return 'schedule__yearSchedule__dayOfWeek__item--specialDay1';
                  case 'special-day-2':
                    return 'schedule__yearSchedule__dayOfWeek__item--specialDay2';
                }
              })(calenderRoutineData.weekly[value]);

              return (
                <Grid item xs={12 / 7} key={value} className={className}>
                  {trans(`ScheduleSetting.defaultSetting.dayOfWeek.${value}`)}
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Box>
    );
  };

  if (
    siteID === undefined ||
    timeZone === '' ||
    calenderData === null ||
    calenderRoutineData === null
  ) {
    return <></>;
  }
  return (
    <Container fixed>
      <Stack spacing={6}>
        {/** 年間スケジュール */}
        <Box>
          <Grid container justifyContent="center">
            {/** タイトル */}
            <Grid item xs={12}>
              <h1>{trans('ScheduleSetting.calendar.title')}</h1>
            </Grid>
            {/** 月毎のカレンダー（標準設定のカレンダーの幅と合わせること） */}
            <Grid item xs={12} lg={10} container columnSpacing={3}>
              <Grid item xs={10}>
                <Calender
                  siteID={siteID}
                  timeZone={timeZone}
                  calenderData={calenderData}
                  calenderRoutineData={calenderRoutineData}
                  setHasCalenderInfoChanged={setHasChangedYearSchedule}
                />
              </Grid>
              {/** 凡例 */}
              <Grid
                item
                xs={2}
                className="schedule__yearSchedule__calender__legend">
                <Stack spacing={1}>
                  <Box className="schedule__yearSchedule__calender__legend__item">
                    <Box className="schedule__yearSchedule__calender__legend__item__weekday" />
                    {trans('ScheduleSetting.calendar.legend.weekday')}
                  </Box>
                  <Box className="schedule__yearSchedule__calender__legend__item">
                    <Box className="schedule__yearSchedule__calender__legend__item__holiday" />
                    {trans('ScheduleSetting.calendar.legend.holiday')}
                  </Box>
                  <Box className="schedule__yearSchedule__calender__legend__item">
                    <Box className="schedule__yearSchedule__calender__legend__item__specialDay1" />
                    {trans('ScheduleSetting.calendar.legend.specialDay1')}
                  </Box>
                  <Box className="schedule__yearSchedule__calender__legend__item">
                    <Box className="schedule__yearSchedule__calender__legend__item__specialDay2" />
                    {trans('ScheduleSetting.calendar.legend.specialDay2')}
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        {/** 標準設定 */}
        <Box>
          <Grid container justifyContent="center">
            {/** タイトル */}
            <Grid item xs={12}>
              <h1>{trans('ScheduleSetting.defaultSetting.title')}</h1>
            </Grid>
            {/** 設定項目（年間スケジュールのカレンダーの幅と合わせること） */}
            <Grid item xs={12} lg={10} container>
              <Grid item xs={10}>
                <Stack spacing={4}>
                  {/** 曜日毎の設定 */}
                  <DayOfWeekSetting />
                  {/** 日毎の設定 */}
                  <SpecificDaySetting
                    routineData={calenderRoutineData}
                    setIsOpenEditDialog={setIsOpenDesignatedAddEditDialog}
                    apiWait={apiWait}
                    timeZone={timeZone}
                    setHasChangedRoutine={setHasChangedRoutine}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Stack>
      {/** 曜日設定変更ダイアログ */}
      <DayOfWeekSettingDialog
        calenderRoutine={calenderRoutineData}
        isOpenDialog={isOpenDayOfWeekSettingDialog}
        setIsOpenDialog={setIsOpenDayOfWeekSettingDialog}
        setIsRoutineChange={setHasChangedRoutine}
      />
      {/** 指定日設定用ダイアログ */}
      <DesignatedDaySettingDialog
        timeZone={timeZone}
        calenderRoutineData={calenderRoutineData}
        isOpenDialog={isOpenDesignatedDayAddDialog}
        setIsOpenDialog={setIsOpenDesignatedAddEditDialog}
        setIsRoutineChange={setHasChangedRoutine}
      />
    </Container>
  );
};

/**
 * 特定日と編集アイコンの表示欄
 * @param props
 * @returns JSX.Element
 */
const SpecifiedDayCell = (props: {
  /** 標準設定の特別日テーブルのインデックス */
  index: number;
  /** 標準設定情報 */
  routineData:
    | apiIF.resParameters.specifiedDate
    | apiIF.resParameters.specifiedWeek;
  /** すべての標準設定情報 */
  allRoutineData: apiIF.res.getSiteCalenderRoutine;
  /** タイムゾーン */
  timeZone: string;
  /** API応答待ち */
  apiWait: boolean;
  /** Editダイアログの状態 */
  isOpenEditDialog: boolean;
  /** Deleteダイアログの状態 */
  isOpenDeleteDialog: boolean;
  /** Editダイアログの状態設定関数 */
  setIsOpenEditDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** Deleteダイアログの状態設定関数 */
  setIsOpenDeleteDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 標準設定変更があるかどうか */
  setHasChangedRoutine: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /**
   * 指定日編集ボタンの処理
   * @param index テーブルのインデックス
   * @param info 編集する標準設定情報
   */
  const handleEditSettingClick = (event: {stopPropagation: () => void}) => {
    event.stopPropagation();
    props.setIsOpenEditDialog(true);
  };

  /**
   * 指定日削除ボタンの処理
   * @param index テーブルのインデックス
   */
  const handleDeleteSettingClick = (event: {stopPropagation: () => void}) => {
    event.stopPropagation();
    props.setIsOpenDeleteDialog(true);
  };

  /**
   * 特定日の種類をチェックして表示する文言を決定する
   * @param schedule 特定日の種類
   * @returns 表示する特定日
   */
  const scheduleTypeTable = (
    schedule:
      | apiIF.resParameters.specifiedDate['schedule']
      | apiIF.resParameters.specifiedWeek['schedule']
  ) => {
    switch (schedule) {
      case 'weekday':
      default: //fall thorough
        return trans(
          'ScheduleSetting.defaultSetting.daySetting.dateType.weekday'
        );
      case 'holiday':
        return trans(
          'ScheduleSetting.defaultSetting.daySetting.dateType.holiday'
        );
      case 'special-day-1':
        return trans(
          'ScheduleSetting.defaultSetting.daySetting.dateType.special1'
        );
      case 'special-day-2':
        return trans(
          'ScheduleSetting.defaultSetting.daySetting.dateType.special2'
        );
    }
  };

  return (
    <>
      <Grid item xs={4} className="schedule__table__body__row__item">
        {scheduleTypeTable(props.routineData.schedule)}
      </Grid>
      <Grid item xs={4} container columnSpacing={2}>
        <Grid
          item
          xs="auto"
          className="schedule__table__body__row__item__operationIcon">
          <IconButton
            size="small"
            onClick={handleEditSettingClick}
            disabled={props.apiWait}>
            <Box className="schedule__table__body__row__item__operationIcon__border">
              <img
                src={iconBlack.EDIT.src}
                alt={`${trans(
                  `ScheduleSetting.defaultSetting.image.alternativeText.${iconBlack.EDIT.alt}`
                )}`}
                title={`${trans(
                  `ScheduleSetting.defaultSetting.image.title.${iconWhite.EDIT.title}`
                )}`}
                className="schedule__table__body__row__item__operationIcon__border__image"
              />
            </Box>
          </IconButton>
        </Grid>
        <Grid
          item
          xs="auto"
          className="schedule__table__body__row__item__operationIcon">
          <IconButton
            size="small"
            onClick={handleDeleteSettingClick}
            disabled={props.apiWait}>
            <Box className="schedule__table__body__row__item__operationIcon__border">
              <img
                src={iconBlack.DELETE.src}
                alt={`${trans(
                  `ScheduleSetting.defaultSetting.image.alternativeText.${iconBlack.DELETE.alt}`
                )}`}
                title={`${trans(
                  `ScheduleSetting.defaultSetting.image.title.${iconWhite.DELETE.title}`
                )}`}
                className="schedule__table__body__row__item__operationIcon__border__image"
              />
            </Box>
          </IconButton>
        </Grid>
      </Grid>
      {/** 指定日設定用ダイアログ */}
      <DesignatedDaySettingDialog
        timeZone={props.timeZone}
        calenderRoutineData={props.allRoutineData}
        isOpenDialog={props.isOpenEditDialog}
        setIsOpenDialog={props.setIsOpenEditDialog}
        tableIndex={props.index}
        editType={'date' in props.routineData ? 'day' : 'week'}
        setIsRoutineChange={props.setHasChangedRoutine}
      />
      {/** 指定日削除用ダイアログ */}
      <DesignatedDayDeleteDialog
        calenderRoutineData={props.allRoutineData}
        isOpenDialog={props.isOpenDeleteDialog}
        setIsOpenDialog={props.setIsOpenDeleteDialog}
        tableIndex={props.index}
        setIsRoutineChange={props.setHasChangedRoutine}
      />
    </>
  );
};

/**
 * 日付設定のテーブルの一列を返す
 * @param props props
 * @returns JSX.Element
 */
const CalenderRoutineTable = (props: {
  /** 標準設定の特別日テーブルのインデックス */
  index: number;
  /** 標準設定情報 */
  routineData:
    | apiIF.resParameters.specifiedDate
    | apiIF.resParameters.specifiedWeek;
  /** すべての標準設定情報 */
  allRoutineData: apiIF.res.getSiteCalenderRoutine;
  /** タイムゾーン */
  timeZone: string;
  /** API応答待ち */
  apiWait: boolean;
  /** 標準設定変更があるかどうか設定関数 */
  setHasChangedRoutine: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  //選択状態のスケジュール行
  const [selectedRow, setSelectedRow] = useState<number | string>('');
  /** 指定日ごとの編集ダイアログの表示 */
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  /** 指定日ごとの削除ダイアログの表示 */
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);

  /** 月名（省略形）一覧 */
  const monthAbbreviationTable = [
    trans('ScheduleSetting.defaultSetting.daySetting.month.january'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.february'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.march'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.april'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.may'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.june'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.july'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.august'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.september'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.october'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.november'),
    trans('ScheduleSetting.defaultSetting.daySetting.month.december'),
  ];
  /** 週番号一覧(省略形) */
  const weekNumberAbbreviationTable = [
    trans('ScheduleSetting.defaultSetting.daySetting.weekNumber.first'),
    trans('ScheduleSetting.defaultSetting.daySetting.weekNumber.second'),
    trans('ScheduleSetting.defaultSetting.daySetting.weekNumber.third'),
    trans('ScheduleSetting.defaultSetting.daySetting.weekNumber.fourth'),
    trans('ScheduleSetting.defaultSetting.daySetting.weekNumber.fifth'),
    trans('ScheduleSetting.defaultSetting.daySetting.weekNumber.last'),
  ];
  /** 曜日（省略形）一覧 */
  const dayOfWeekAbbreviationTable = [
    trans('ScheduleSetting.defaultSetting.dayOfWeek.monday'),
    trans('ScheduleSetting.defaultSetting.dayOfWeek.tuesday'),
    trans('ScheduleSetting.defaultSetting.dayOfWeek.wednesday'),
    trans('ScheduleSetting.defaultSetting.dayOfWeek.thursday'),
    trans('ScheduleSetting.defaultSetting.dayOfWeek.friday'),
    trans('ScheduleSetting.defaultSetting.dayOfWeek.saturday'),
    trans('ScheduleSetting.defaultSetting.dayOfWeek.sunday'),
  ];

  const className = ((
    dayType: 'weekday' | 'holiday' | 'special-day-1' | 'special-day-2'
  ) => {
    switch (dayType) {
      case 'weekday':
        return 'schedule__table__body__row--weekday';
      case 'holiday':
        return 'schedule__table__body__row--holiday';
      case 'special-day-1':
        return 'schedule__table__body__row--specialDay1';
      case 'special-day-2':
        return 'schedule__table__body__row--specialDay2';
    }
  })(props.routineData.schedule);

  /**
   * スケジュール行が選択状態か判定
   * @param row 行
   */
  const selectHandle = (row: number) => {
    //ダイアログが開いている場合は処理を行わない
    if (isOpenEditDialog === true || isOpenDeleteDialog === true) {
      return;
    } else if (row === selectedRow) {
      setSelectedRow('');
    } else {
      setSelectedRow(row);
    }
  };

  //日付での指定の場合
  if ('date' in props.routineData) {
    /** テーブルに表示する月日 */
    const [tempMonth, tempDay] = props.routineData.date.split('-');
    return (
      <Grid
        item
        xs={12}
        container
        alignItems="center"
        onClick={e => {
          selectHandle(props.index);
        }}
        className={`schedule__table__body__row ${
          props.index === selectedRow
            ? 'schedule__table__body__row--selected'
            : className
        }`}>
        {/** 日付 */}
        <Grid item xs={4} className="schedule__table__body__row__item">
          {trans(
            'ScheduleSetting.defaultSetting.daySetting.table.body.date.day',
            {
              day: tempDay,
              month: monthAbbreviationTable[Number(tempMonth) - 1],
            }
          )}
        </Grid>
        <SpecifiedDayCell
          index={props.index}
          routineData={props.routineData}
          allRoutineData={props.allRoutineData}
          timeZone={props.timeZone}
          apiWait={props.apiWait}
          isOpenEditDialog={isOpenEditDialog}
          isOpenDeleteDialog={isOpenDeleteDialog}
          setIsOpenEditDialog={setIsOpenEditDialog}
          setIsOpenDeleteDialog={setIsOpenDeleteDialog}
          setHasChangedRoutine={props.setHasChangedRoutine}
        />
      </Grid>
    );
  }
  //週番号での指定の場合
  return (
    <Grid
      item
      xs={12}
      container
      alignItems="center"
      onClick={e => {
        selectHandle(props.index);
      }}
      className={`schedule__table__body__row ${
        props.index === selectedRow
          ? 'schedule__table__body__row--selected'
          : className
      }`}>
      {/** 日付 */}
      <Grid item xs={4} className="schedule__table__body__row__item">
        {trans(
          'ScheduleSetting.defaultSetting.daySetting.table.body.date.week',
          {
            //テーブルに表示する週番号の省略形
            weekNumber:
              weekNumberAbbreviationTable[
                weekNumberTable.indexOf(props.routineData['week-number'])
              ],
            //テーブルに表示する曜日の省略形
            dayOfWeek:
              dayOfWeekAbbreviationTable[
                dayOfWeekTable.indexOf(props.routineData['day-of-week'])
              ],
            //テーブルに表示する月の省略形
            month:
              monthAbbreviationTable[
                monthTable.indexOf(props.routineData.month)
              ],
          }
        )}
      </Grid>
      <SpecifiedDayCell
        routineData={props.routineData}
        index={props.index}
        allRoutineData={props.allRoutineData}
        timeZone={props.timeZone}
        apiWait={props.apiWait}
        isOpenEditDialog={isOpenEditDialog}
        isOpenDeleteDialog={isOpenDeleteDialog}
        setIsOpenEditDialog={setIsOpenEditDialog}
        setIsOpenDeleteDialog={setIsOpenDeleteDialog}
        setHasChangedRoutine={props.setHasChangedRoutine}
      />
    </Grid>
  );
};

/**
 * 日毎の設定
 * @param param0 param0
 * @returns JSX.Element
 */
const SpecificDaySetting = ({
  timeZone,
  routineData,
  setIsOpenEditDialog,
  setHasChangedRoutine,
  apiWait,
}: {
  /** タイムゾーン */
  timeZone: string;
  /** 標準設定情報 */
  routineData: apiIF.res.getSiteCalenderRoutine;
  /** 編集ダイアログを表示するかどうか */
  setIsOpenEditDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 標準設定変更があるかどうか */
  setHasChangedRoutine: React.Dispatch<React.SetStateAction<boolean>>;
  /** API応答待ち */
  apiWait: boolean;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /**
   * 特別指定日追加ボタンの処理
   */
  const handleAddClick = () => {
    setIsOpenEditDialog(true);
  };

  /**
   * 特定日設定を時系列順に並び替え
   * @param  ソート対象の特定日データ設定
   * @returns ソートした特定日データ
   */
  const calenderRoutineDataSort = (
    a: apiIF.res.getSiteCalenderRoutine['specified-day'][0],
    b: apiIF.res.getSiteCalenderRoutine['specified-day'][0]
  ) => {
    //比較データの初期値を設定
    let aData = 0;
    let bData = 0;

    if ('date' in a && 'date' in b) {
      //両方とも特別日設定の場合
      //dateを数値に変換して比較する
      aData = Number(a.date.replace('-', ''));
      bData = Number(b.date.replace('-', ''));
    } else if ('month' in a && 'month' in b) {
      //両方とも特別週間設定の場合
      //月名一覧からindex番号を取得して比較する
      aData = monthTable.indexOf(a.month);
      bData = monthTable.indexOf(b.month);
      //monthが同じ場合、week-numberを比較する
      if (aData === bData) {
        //週番号一覧からindex番号を取得して比較する
        aData = weekNumberTable.indexOf(a['week-number']);
        bData = weekNumberTable.indexOf(b['week-number']);
        //week-numberが同じ場合、day-of-weekを比較する
        if (aData === bData) {
          //曜日一覧からindex番号を取得して比較する
          aData = dayOfWeekTable.indexOf(a['day-of-week']);
          bData = dayOfWeekTable.indexOf(b['day-of-week']);
        }
      }
    } else if ('date' in a && 'month' in b) {
      //aが特別日設定、bが特別週間設定の場合
      //specifiedDateのdateとspecifiedMonthのmonthから月数を抽出して比較する
      aData = Number(a.date.slice(0, 2));
      bData = monthTable.indexOf(b.month) + 1;
      //同じ月の場合、特別日設定側が上に来るようにする
      if (aData === bData) {
        bData = bData + 1;
      }
    } else if ('month' in a && 'date' in b) {
      //aが特別週間設定設定、bが特別日設定の場合
      //specifiedDateのdateとspecifiedMonthのmonthから月数を抽出して比較する
      aData = monthTable.indexOf(a.month) + 1;
      bData = Number(b.date.slice(0, 2));
      //同じ月の場合、特別日設定側が上に来るようにする
      if (aData === bData) {
        aData = aData + 1;
      }
    }
    return aData > bData ? 1 : -1;
  };

  return (
    <Box>
      <Grid container rowSpacing={2} alignItems="center">
        {/** タイトル */}
        <Grid item xs>
          <h2>{trans('ScheduleSetting.defaultSetting.daySetting.title')}</h2>
        </Grid>
        {/** 追加アイコン */}
        <Grid item xs="auto">
          <AddButton
            onClick={handleAddClick}
            id="mv-btnAddDaySetting"
            disabled={
              apiWait ||
              routineData['specified-day'].length >=
                limit.SCHEDULE_DEFAULT_DAY_MAX
            }
            buttonText={trans(
              'ScheduleSetting.defaultSetting.daySetting.buttonTxt.add'
            )}
          />
        </Grid>
        {/** 一覧 */}
        <Grid item xs={12}>
          <Stack component={Paper} className="schedule__table">
            {/** ヘッダ */}
            <Grid
              container
              alignItems="center"
              className="schedule__table__header">
              {/** 日付 */}
              <Grid item xs={4} className="schedule__table__header__item">
                {trans(
                  'ScheduleSetting.defaultSetting.daySetting.table.header.date'
                )}
              </Grid>
              {/** 特定日 */}
              <Grid item xs={4} className="schedule__table__header__item">
                {trans(
                  'ScheduleSetting.defaultSetting.daySetting.table.header.day'
                )}
              </Grid>
              {/** 操作アイコン */}
              <Grid item xs={4} className="schedule__table__header__item">
                {/** 空間を作るために必要 */}
              </Grid>
            </Grid>
            {/** ボディ */}
            <Grid container className="schedule__table__body--daySetting">
              {routineData['specified-day'].length === 0 ? (
                // 標準設定がない場合
                <Box className="schedule__table__body--daySetting__noDataText">
                  {trans('ScheduleSetting.defaultSetting.noDataText')}
                </Box>
              ) : (
                // 標準設定がある場合
                routineData['specified-day']
                  .sort(calenderRoutineDataSort)
                  .map((data, index) => {
                    return (
                      <CalenderRoutineTable
                        key={index}
                        index={index}
                        routineData={data}
                        allRoutineData={routineData}
                        timeZone={timeZone}
                        apiWait={apiWait}
                        setHasChangedRoutine={setHasChangedRoutine}
                      />
                    );
                  })
              )}
            </Grid>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ScheduleSetting;
